import { Component } from '@theme/component';

const STORAGE_KEY = 'kc:recently-viewed';
const HISTORY_CAP = 12;

/**
 * Fills the "Recently viewed" row from the shopper's own browsing history.
 *
 * History lives in localStorage only -- it is never sent anywhere. Every access
 * is guarded because Safari private browsing throws on setItem, and a storage
 * failure must degrade to a hidden row rather than a broken page.
 */
class RecentlyViewed extends Component {
  connectedCallback() {
    super.connectedCallback();

    const currentHandle = this.dataset.currentHandle;
    const history = this.#readHistory();

    // Record this visit before rendering, so the row reflects what came before.
    if (currentHandle) this.#writeHistory([currentHandle, ...history.filter((h) => h !== currentHandle)]);

    const max = Number.parseInt(this.dataset.max ?? '5', 10);
    const toRender = history.filter((handle) => handle !== currentHandle).slice(0, max);

    if (toRender.length === 0) return;

    this.#render(toRender);
  }

  /** @returns {string[]} */
  #readHistory() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter((h) => typeof h === 'string') : [];
    } catch {
      return [];
    }
  }

  /** @param {string[]} handles */
  #writeHistory(handles) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(handles.slice(0, HISTORY_CAP)));
    } catch {
      // Private browsing or a full quota. The row still renders from whatever
      // was already stored; only the recording of this visit is lost.
    }
  }

  /** @param {string[]} handles */
  async #render(handles) {
    const list = this.querySelector('[data-recently-viewed-list]');
    const template = this.dataset.cardUrl;
    if (!list || !template) return;

    const cards = await Promise.all(
      handles.map(async (handle) => {
        try {
          const response = await fetch(template.replace('PRODUCT_HANDLE', encodeURIComponent(handle)));
          if (!response.ok) return null;
          const html = (await response.text()).trim();
          return html.length > 0 ? { handle, html } : null;
        } catch {
          // Product deleted, unpublished, or the network failed. Drop this one
          // card and keep the rest of the row.
          return null;
        }
      })
    );

    const usable = cards.filter((card) => card !== null);
    if (usable.length === 0) return;

    for (const card of usable) {
      const item = document.createElement('li');
      item.className = 'recently-viewed__item';
      item.innerHTML = card.html;
      list.append(item);
    }

    this.removeAttribute('hidden');
  }
}

if (!customElements.get('recently-viewed')) {
  customElements.define('recently-viewed', RecentlyViewed);
}
